import { useState, ReactNode } from 'react';
import type { NavigateFunction, NavigateOptions, To } from 'react-router-dom';
import { SessionContext, type ChangeSelectedResidentOptions } from '@contexts';
import { helpers, Enums } from "@shared";
import { sunboundPayProxy } from '@proxies';
import { redirectWithAuthTransfer } from '@stores';
import type { RawSession, SessionData, Account, SelectedAccount } from '@app-types';

const useSession = () => {
    const [session, setSession] = useState<RawSession | null>(null);
    const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | null>(null);
    const [sessionData, setSessionData] = useState<SessionData | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);

    const initSession = (session: RawSession | null) => {

        setSession(session);
        if (session != null && session.accounts) {
            let idAccount: number | null = null, residentName = ``;
            const {
                accounts,
                payorPreferences: { selectedAccount: prefSelectedAccount = null }
            } = session;

            idAccount = selectedAccount?.idAccount ?? prefSelectedAccount ?? null;

            const foundAccount = idAccount && accounts.find(a => a.idAccount === idAccount);
            if (!foundAccount) {
                // in the absence of a selected account:
                // - assumes there is always at least one account
                // - prefers the earliest created account
                const sortedAccounts = [...accounts].sort((a, b) => a.idAccount - b.idAccount);
                const firstAccount = sortedAccounts[0];

                if (firstAccount) {
                    ({ idAccount, residentName } = firstAccount);
                }
            } else {
                ({ idAccount, residentName } = foundAccount);
            }

            if (idAccount) {
                setSelectedAccount({ idAccount, residentName });
                setSession(session);
                setSessionData(helpers.sessionData(session, idAccount));
            }

            setAccounts(accounts);
        }
    };

    const changeSelectedResident = async (idAccount: number, navigate?: NavigateFunction, to?: To, options?: ChangeSelectedResidentOptions & NavigateOptions) => {
        if (!session) return;

        const acc = helpers.sessionData(session, idAccount);
        if (!acc) return; // For TS compiler; selectedAccount should always exist in accounts
        const { residentName = ``, community } = acc;

        // Check if we need to redirect to a different subdomain
        if (options?.ensureCommunitySubdomain && community?.shortName) {
            const targetHost = helpers.getCommunitySubdomain(community.shortName);
            if (window.location.host !== targetHost) {
                // Cross-subdomain: save preference first, then redirect
                try {
                    await sunboundPayProxy.updatePayorPreferences(Enums.Preference.selectedAccount, idAccount);
                } catch (error) {
                    console.log(error);
                    return;
                }

                const targetPath = typeof to === `string` ? to : (to?.pathname ?? `/`);
                await redirectWithAuthTransfer(targetHost, targetPath);
                return;
            }
        }

        // Same subdomain or no community: optimistic update and navigate first,
        // then save in background
        setSessionData(acc);
        setSelectedAccount({ idAccount, residentName });

        if (navigate && to) {
            navigate(to, options);
        }

        try {
            await sunboundPayProxy.updatePayorPreferences(Enums.Preference.selectedAccount, idAccount);
        } catch (error) {
            console.log(error);
        }
    };

    const logout = () => {
        setSelectedAccount(null);
        setSession(null);
        setSessionData(null);
        setAccounts([]);
    };

    return {
        rawSession: session,
        session: sessionData,
        accounts,
        setSession: initSession,
        selectedAccount,
        changeSelectedResident,
        logout
    };
};

interface SessionProviderProps {
    children: ReactNode;
}

function SessionProvider({ children }: SessionProviderProps) {
    const value = useSession();
    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export default SessionProvider;
